/**
 * DescriptionHeader component.
 */
import Addon from '@shared/classes/addon';
import Corner from '@shared/classes/corner';
import Context from '@shared/components/utils/context/descriptionContext';
import HeaderContainer from '@components/atoms/container/descriptionHeaderContainer';
import HeroContainer from '@components/atoms/container/descriptionHeroContainer';
import DescriptionImage from '@components/atoms/images/descriptionImage';
import Logo from '@components/atoms/images/descriptionLogo';
import ParentalUtils from '@shared/utils/parental';
import PhotoDefault from '@shared/assets/images/png/photoDefault.png';
import Tick from '@components/elements/addons/descriptionAddonTick';
import UpgradablePlan from '@components/elements/plan/upgradablePlan';
import { DELAY, DURATION } from '@shared/types/constants/animations';
import { useContext, useEffect, useMemo, useState } from 'react';

/**
 * Constants.
 *
 * @constant DESCRIPTION_TIME: The time in milliseconds to display the description background.
 */
const DESCRIPTION_TIME = 300;

/**
 * Displays the photo, upgradable plan, and logo.
 */
export default function DescriptionHeader() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Context
  const { asset, display, metadata } = useContext(Context);
  const { upgradablePlan: plan } = metadata ?? {};

  // State
  const [image, setImage] = useState<string | null>(null);
  const [logo, setLogo] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  /**
   * Handles the background and logo image loading.
   */
  useEffect(() => {
    setImage(null);
    setLogo(null);
    setLoaded(false);
    const imageTimeout = setTimeout(() => {
      const newLogo =
        asset?.channel?.logoWhite ||
        (asset as Addon | Corner)?.logoWhite ||
        (asset as Addon | Corner)?.logoColor ||
        null;
      setImage(asset?.background || null);
      setLogo(newLogo);
    }, DESCRIPTION_TIME);
    return () => {
      clearTimeout(imageTimeout);
    };
  }, [asset]);

  /**
   * Handles the logo image loading.
   */
  useEffect(() => {
    setLoaded(false);
    if (!logo) return;
    const img = new Image();
    const onLoad = () => setLoaded(true);
    const onError = () => setLoaded(false);
    img.addEventListener('load', onLoad);
    img.addEventListener('error', onError);
    img.src = logo;
    return () => {
      img.removeEventListener('load', onLoad);
      img.removeEventListener('error', onError);
    };
  }, [logo]);

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  const { finalImage, finalLogo, displayPlan, displayLogo, displayTick } =
    useMemo(() => {
      return {
        finalImage: ParentalUtils.isAdultRestricted(asset)
          ? PhotoDefault
          : image,
        finalLogo: logo ?? '',
        displayPlan: plan && loaded && display,
        displayLogo: logo && loaded && display,
        displayTick: loaded && display,
      };
    }, [asset, display, image, loaded, logo, plan]);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return (
    <HeaderContainer
      display={display}
      delay={display ? DELAY.DESCRIPTION_HEADER : 0}
      duration={display ? DURATION.DESCRIPTION_HEADER : 0}
    >
      {finalImage ? <DescriptionImage src={finalImage} /> : null}
      <HeroContainer display={loaded}>
        {displayPlan ? <UpgradablePlan plan={plan} /> : null}
        {displayLogo ? <Logo src={finalLogo} /> : null}
        {displayTick ? <Tick asset={asset as Addon} /> : null}
      </HeroContainer>
    </HeaderContainer>
  );
}
